import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  ApprovedPharmacyFilters,
  ApprovedPharmacyListItem,
  AssignTeleSalesEmployeeRequest,
} from '../../pages/admin-dashboard/admin-approved-pharmacies/approved-pharmacies.model';
import { AuthService } from '../auth-service';
import { PharmacyDetails } from '../../pages/admin-dashboard/admin-approved-pharmacies/approved-ph-details/approved-ph-details.model';

@Injectable({ providedIn: 'root' })
export class ApprovedPharmaciesService {
  private http = inject(HttpClient);
  private baseUrl = environment.pharmacyMgtApiUrl;
  private userUrl = environment.userMgtApiUrl;
  private workFlowUrl = environment.workflowApiUrl;

  private authService = inject(AuthService);

  getApprovedPharmacies(
    filters: ApprovedPharmacyFilters,
  ): Observable<{ total: number; list: ApprovedPharmacyListItem[] }> {
    let params = new HttpParams();

    if (filters.pharmacyId) params = params.set('pharmacyId', filters.pharmacyId);
    if (filters.representativeId) params = params.set('representativeId', filters.representativeId);
    if (filters.statusIds?.length) filters.statusIds.forEach((id) => (params = params.append('statusIds', id)));
    if (filters.pharmacyOwnerId) params = params.set('pharmacyOwnerId', filters.pharmacyOwnerId);
    if (filters.assignedToId) params = params.set('assignedToId', filters.assignedToId);
    if (filters.governorateIds?.length)
      filters.governorateIds.forEach((id) => (params = params.append('governorateIds', id)));
    if (filters.pharmacySegmentIds?.length)
      filters.pharmacySegmentIds.forEach((id) => (params = params.append('pharmacySegmentIds', id)));
    if (filters.cityId) params = params.set('cityId', filters.cityId);
    if (filters.regionId) params = params.set('regionId', filters.regionId);
    if (filters.minCredit !== undefined) params = params.set('minCredit', filters.minCredit);
    if (filters.maxCredit !== undefined) params = params.set('maxCredit', filters.maxCredit);
    if (filters.createdOnFrom) params = params.set('createdOnFrom', filters.createdOnFrom);
    if (filters.createdOnTo) params = params.set('createdOnTo', filters.createdOnTo);
    if (filters.lastModifiedOnFrom) params = params.set('lastModifiedOnFrom', filters.lastModifiedOnFrom);
    if (filters.lastModifiedOnTo) params = params.set('lastModifiedOnTo', filters.lastModifiedOnTo);
    if (filters.orderBy) params = params.set('orderBy', filters.orderBy);
    if (filters.orderDir) params = params.set('orderDir', filters.orderDir);
    params = params.set('pageNum', filters.pageNum ?? 0);
    params = params.set('pageSize', filters.pageSize ?? 25);

    return this.http.get<{ total: number; list: ApprovedPharmacyListItem[] }>(`${this.baseUrl}/pharmacies/approved`, {
      params,
      headers: this.getHeaders(),
    });
  }

  teleSalesEmployeeAutoComplete(quickSearch: string): Observable<{ list: { id: number; fullName: string }[] }> {
    return this.http.get<{ list: { id: number; fullName: string }[] }>(
      `${this.userUrl}/pharmacy/telesale/employees/auto/complete`,
      {
        params: new HttpParams().set('quickSearch', quickSearch),
        headers: this.getHeaders(),
      },
    );
  }

  assignTeleSalesEmployee(pharmacyId: number, body: AssignTeleSalesEmployeeRequest): Observable<void> {
    return this.http.put<void>(`${this.workFlowUrl}/pharmacies/${pharmacyId}/approved/assign`, body, {
      headers: this.getHeaders(),
    });
  }

  getPharmacyDetails(pharmacyId: number): Observable<PharmacyDetails> {
  const headers = this.getHeaders();

  return this.http.get<PharmacyDetails>(
    `${this.baseUrl}/pharmacies/${pharmacyId}`,
    { headers }
  );
}
  setNegativeOrderReason(pharmacyId: number, reasonId: number): Observable<void> {
    return this.http.put<void>(
      `${this.baseUrl}/pharmacies/${pharmacyId}/negative/order/reason`,
      { reasonId },
      { headers: this.getHeaders() },
    );
  }

  suspendPharmacy(pharmacyId: number): Observable<void> {
    return this.http.patch<void>(`${this.baseUrl}/pharmacies/${pharmacyId}/suspend`, null, {
      headers: this.getHeaders(),
    });
  }

  unsuspendPharmacy(pharmacyId: number): Observable<void> {
    return this.http.patch<void>(`${this.baseUrl}/pharmacies/${pharmacyId}/unsuspend`, null, {
      headers: this.getHeaders(),
    });
  }

  private getHeaders(): HttpHeaders {
    const token = this.authService.getAccessToken();
    let headers = new HttpHeaders();
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }
    return headers;
  }
}
